import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar transparent={false} />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画可能已被移除或链接无效</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Generate fake chapters
  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(Date.now() - i * 86400000 * 3).toISOString().split('T')[0],
    isNew: i < 3,
  }));

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 5);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩漫画免费阅读`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: '2024-01-01',
        dateModified: comic.updatedAt,
        image: comic.cover,
        articleSection: comic.categoryName,
        isPartOf: { '@type': 'WebSite', name: 'ManhwaFun' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://costrebate.com/' },
          { '@type': 'ListItem', position: 2, name: comic.categoryName, item: `http://costrebate.com/category/${comic.category}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `http://costrebate.com/comic/${comic.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫画免费阅读_好看的韩国漫画大全`}
        description={`${comic.title}，${comic.description}。韩漫画免费阅读，看漫画下拉式，更新及时体验流畅。作者：${comic.author}，共${comic.chapters}话。`}
        keywords={`${comic.title},${comic.categoryName},韩漫画免费阅读,看漫画下拉式,好看的韩国漫画大全`}
        canonical={`http://costrebate.com/comic/${comic.slug}`}
        ogTitle={`${comic.title} - 韩漫画免费阅读`}
        ogDescription={`${comic.title}，${comic.description}。作者：${comic.author}，${comic.status}，共${comic.chapters}话。`}
        ogUrl={`http://costrebate.com/comic/${comic.slug}`}
        schemaData={schemaData}
      />

      <Navbar transparent={false} />

      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors">{comic.categoryName}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Info */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              {/* Cover */}
              <div className="md:w-1/3 lg:w-1/4 flex-shrink-0">
                <div className="relative">
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 z-10">
                    {comic.categoryName}
                  </span>
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫画免费阅读`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-3">
                  {comic.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-5 max-w-xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-400 mb-0.5">作者</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.author}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-400 mb-0.5">评分</p>
                    <p className="text-sm font-medium text-accent-500">
                      <i className="ri-star-fill mr-1 text-xs"></i>
                      {comic.rating.toFixed(1)}
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-400 mb-0.5">总话数</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.chapters}话</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-400 mb-0.5">状态</p>
                    <p className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-500' : 'text-foreground-700'}`}>
                      {comic.status}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${comic.slug}/read?chapter=1`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i>
                    从第一话开始阅读
                  </Link>
                  <Link
                    to={`/comic/${comic.slug}/read?chapter=${comic.chapters}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-skip-forward-line"></i>
                    阅读最新话
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              章节列表
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {chapters.map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}/read?chapter=${ch.num}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-50/80 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    {ch.isNew && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                        NEW
                      </span>
                    )}
                    <span className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors">
                      {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </Link>
              ))}
            </div>

            {comic.chapters > 20 && (
              <div className="text-center mt-6">
                <p className="text-sm text-foreground-400">
                  显示最新 20 话，共 {comic.chapters} 话
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}